import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Buildings, UsersThree, CurrencyGbp, TrendUp, ChartBar, Pulse, Lightning,
  ArrowRight, Folder, Briefcase, ClipboardText, Receipt, Wrench, Truck,
  ShieldCheck, Crown, Timer, Gauge, Eye, Plugs, Globe, CaretRight
} from "@phosphor-icons/react";
import { toast } from "sonner";
import DeploymentHealthCard from "../components/DeploymentHealthCard";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line, CartesianGrid } from "recharts";

const CHART_COLORS = ["#135DD4", "#D6EF37", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];
const PLAN_COLORS = { starter: "#D6EF37", professional: "#135DD4", enterprise: "#8b5cf6", one_time_licence: "#10b981" };
const PLAN_LABELS = { starter: "Starter", professional: "Professional", enterprise: "Enterprise", one_time_licence: "One-Time" };

export default function SaasDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await API.get("/saas/dashboard");
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dashboard");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );

  if (!data) return (
    <div className="text-center py-16">
      <p className="text-muted-foreground">Unable to load SaaS dashboard data.</p>
      <Button className="mt-4" onClick={fetchData}>Retry</Button>
    </div>
  );

  const planData = Object.entries(data.subscriptions.plan_distribution).map(([key, val]) => ({
    name: PLAN_LABELS[key] || key,
    value: val,
    color: PLAN_COLORS[key] || CHART_COLORS[0],
  }));

  const usageData = [
    { name: "Projects", value: data.platform_usage.projects },
    { name: "Jobs", value: data.platform_usage.jobs },
    { name: "Work Orders", value: data.platform_usage.work_orders },
    { name: "Invoices", value: data.platform_usage.invoices },
    { name: "Assets", value: data.platform_usage.assets },
    { name: "Suppliers", value: data.platform_usage.suppliers },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo] tracking-tight">SaaS Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Platform overview and key metrics</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/platform-dashboard")} data-testid="view-platform-dashboard-btn">
          <Eye size={16} className="mr-1.5" /> View Platform Dashboard
        </Button>
      </div>

      <DeploymentHealthCard />

      {/* KPI Cards Row 1 - Revenue & Tenants */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={CurrencyGbp} label="Monthly Revenue" value={`£${data.revenue.mrr.toLocaleString()}`} subtitle={`ARR: £${data.revenue.arr.toLocaleString()}`} color="text-emerald-600 dark:text-emerald-400" bg="bg-emerald-50 dark:bg-emerald-950/30" />
        <KpiCard icon={Buildings} label="Total Tenants" value={data.tenants.total} subtitle={`${data.tenants.active} active`} color="text-primary" bg="bg-primary/5" href="/platform/tenants" />
        <KpiCard icon={UsersThree} label="Total Users" value={data.users.total} subtitle={`+${data.users.new_30d} this month`} color="text-blue-600 dark:text-blue-400" bg="bg-blue-50 dark:bg-blue-950/30" />
        <KpiCard icon={Timer} label="Active Trials" value={data.subscriptions.trial_active} subtitle={`${data.subscriptions.trial_expired} expired`} color="text-amber-600 dark:text-amber-400" bg="bg-amber-50 dark:bg-amber-950/30" href="/platform/trial-conversions" />
        <KpiCard icon={Pulse} label="API Requests" value={data.api_requests_30d.toLocaleString()} subtitle="Last 30 days" color="text-purple-600 dark:text-purple-400" bg="bg-purple-50 dark:bg-purple-950/30" href="/platform/api-usage" />
        <KpiCard icon={Plugs} label="Webhooks" value={data.webhook_count} subtitle="Active hooks" color="text-cyan-600 dark:text-cyan-400" bg="bg-cyan-50 dark:bg-cyan-950/30" href="/platform/webhooks" />
      </div>

      {/* Platform Usage Cards */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        <UsageCard icon={Folder} label="Projects" value={data.platform_usage.projects} />
        <UsageCard icon={Briefcase} label="Jobs" value={data.platform_usage.jobs} />
        <UsageCard icon={ClipboardText} label="Work Orders" value={data.platform_usage.work_orders} />
        <UsageCard icon={Receipt} label="Invoices" value={data.platform_usage.invoices} />
        <UsageCard icon={Wrench} label="Assets" value={data.platform_usage.assets} />
        <UsageCard icon={Truck} label="Suppliers" value={data.platform_usage.suppliers} />
      </div>

      {/* Charts & Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Subscription Distribution */}
        <div className="border border-border rounded-lg p-5 bg-card">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><Crown size={16} className="text-primary" /> Subscription Plans</h3>
          {planData.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={160}>
                <PieChart>
                  <Pie data={planData} cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={3} dataKey="value">
                    {planData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {planData.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                      <span className="text-muted-foreground">{p.name}</span>
                    </div>
                    <span className="font-semibold">{p.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">No subscription data</p>
          )}
        </div>

        {/* Platform Usage Chart */}
        <div className="border border-border rounded-lg p-5 bg-card">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><ChartBar size={16} className="text-primary" /> Platform Usage</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={usageData} barSize={24}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={30} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--border)" }} />
              <Bar dataKey="value" fill="#135DD4" radius={[4, 4, 0, 0]}>
                {usageData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent Tenants */}
        <div className="border border-border rounded-lg p-5 bg-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold flex items-center gap-2"><Buildings size={16} className="text-primary" /> Recent Tenants</h3>
            <Link to="/platform/tenants" className="text-xs text-primary hover:underline flex items-center gap-0.5">View all <CaretRight size={12} /></Link>
          </div>
          {data.recent_tenants.length > 0 ? (
            <div className="space-y-2">
              {data.recent_tenants.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded-md bg-muted/30 border border-border">
                  <div>
                    <p className="text-sm font-medium">{t.name}</p>
                    <p className="text-[10px] text-muted-foreground">{t.user_count} users · {t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}</p>
                  </div>
                  <Badge className={`text-[10px] capitalize ${t.plan === "enterprise" ? "bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400" : t.plan === "professional" ? "bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400" : "bg-cyan-100 text-cyan-700 dark:bg-cyan-950/30 dark:text-cyan-400"}`}>
                    {PLAN_LABELS[t.plan] || t.plan}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">No tenants yet</p>
          )}
        </div>
      </div>

      {/* Top Tenants Table */}
      {data.top_tenants.length > 0 && (
        <div className="border border-border rounded-lg p-5 bg-card">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><TrendUp size={16} className="text-primary" /> Top Tenants by Usage</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-4 py-2 font-semibold">Tenant</th>
                  <th className="text-left px-4 py-2 font-semibold">Plan</th>
                  <th className="text-center px-4 py-2 font-semibold">Users</th>
                  <th className="text-center px-4 py-2 font-semibold">Projects</th>
                  <th className="text-center px-4 py-2 font-semibold">Jobs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.top_tenants.map((t, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2.5 font-medium">{t.name}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className="text-[10px] capitalize">{PLAN_LABELS[t.plan] || t.plan}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-center">{t.users}</td>
                    <td className="px-4 py-2.5 text-center">{t.projects}</td>
                    <td className="px-4 py-2.5 text-center">{t.jobs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* NEW ENHANCED METRICS */}
      
      {/* MRR Trend Chart */}
      {data.mrr_trend && data.mrr_trend.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold font-[Chivo] mb-4">Monthly Recurring Revenue Trend</h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data.mrr_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="month" stroke="#888" style={{ fontSize: '12px' }} />
              <YAxis stroke="#888" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                formatter={(value) => [`£${value.toLocaleString()}`, 'MRR']}
              />
              <Line type="monotone" dataKey="mrr" stroke="#135DD4" strokeWidth={3} dot={{ fill: '#135DD4', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Churn Rate & Top Revenue Tenants Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Churn Rate Card */}
        {data.churn_rate !== undefined && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold font-[Chivo]">Churn Rate</h2>
              <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <TrendUp size={20} className="text-destructive" weight="duotone" />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-4xl font-bold font-[Chivo] text-destructive">{data.churn_rate}%</p>
              <p className="text-sm text-muted-foreground">Monthly churn rate</p>
              <div className="pt-4 border-t border-border mt-4">
                <p className="text-xs text-muted-foreground">
                  Lower churn rates indicate better customer retention and satisfaction
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Top Revenue Tenants */}
        {data.top_revenue_tenants && data.top_revenue_tenants.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold font-[Chivo] mb-4">Top Revenue Tenants</h2>
            <div className="space-y-3">
              {data.top_revenue_tenants.map((tenant, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{tenant.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{tenant.plan}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-primary">£{tenant.monthly_revenue}</p>
                    <p className="text-xs text-muted-foreground">/month</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tenant Growth Chart */}
      {data.growth_data && data.growth_data.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold font-[Chivo] mb-4">Tenant Growth (New vs. Churned)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.growth_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="month" stroke="#888" style={{ fontSize: '12px' }} />
              <YAxis stroke="#888" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="new" fill="#10b981" name="New Tenants" />
              <Bar dataKey="churned" fill="#ef4444" name="Churned" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Quick Links */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <QuickLink icon={Buildings} label="Manage Tenants" to="/platform/tenants" />
        <QuickLink icon={Crown} label="Subscription Plans" to="/platform/plans" />
        <QuickLink icon={ChartBar} label="Trial Conversions" to="/platform/trial-conversions" />
        <QuickLink icon={CurrencyGbp} label="Revenue & Growth" to="/platform/revenue" />
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, subtitle, color, bg, href }) {
  const content = (
    <div className={`border border-border rounded-lg p-4 bg-card ${href ? "card-lift hover:border-primary/40 cursor-pointer group" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">{label}</span>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${bg}`}>
          <Icon size={18} className={color} weight="duotone" />
        </div>
      </div>
      <p className="text-2xl font-bold font-[Chivo] tracking-tight">{value}</p>
      {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  );
  return href ? <Link to={href}>{content}</Link> : content;
}

function UsageCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-card text-center">
      <Icon size={20} className="mx-auto text-muted-foreground mb-1" weight="duotone" />
      <p className="text-lg font-bold font-[Chivo]">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

function QuickLink({ icon: Icon, label, to }) {
  return (
    <Link to={to} className="border border-border rounded-lg p-4 bg-card flex items-center gap-3 card-lift hover:border-primary/40 group">
      <div className="h-9 w-9 rounded-lg bg-primary/5 flex items-center justify-center">
        <Icon size={18} className="text-primary" weight="duotone" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium">{label}</p>
      </div>
      <CaretRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
    </Link>
  );
}
